import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { projects } from "../data/projects";
import Header from "../components/Header";
import ScrollProgress from "../components/ScrollProgress";
import PortfolioSection from "../components/PortfolioSection";
import { fetchTitles } from "../lib/titlesApi";
import { fetchSettings, fetchCustomProjects, fetchStaticOverrides, fetchTypography } from "../lib/settingsApi";
import { loadGoogleFont } from "../lib/googleFontLoader";
import { resolveStaticTitle } from "../lib/titleResolver";
import { useTransition, CROSSFADE_MS } from "../contexts/TransitionContext";

// Key used to persist the originating project across a case-study → home
// browser-back navigation. We set history.scrollRestoration='manual' to defeat
// the iOS Safari "lands on frame 2" bug, but that also disables the native
// scroll restore on POP. We re-implement it ourselves via sessionStorage.
//
// STABLE KEY (not positional): admin visibility/order + custom projects
// change the visible-list positions between click and return, so a
// section-XX id captured at click time won't match the row that lives
// there at return time. We store the project's slug instead — resolved to
// a section id AFTER the async admin fetches have populated
// `visibleProjects` (see the effect keyed on [visibleProjects] below).
// NOTE: the value stored is only a project slug — no tokens, no PII.
const RETURN_SLUG_KEY = "portfolio-return-project-slug";
// Legacy key from earlier positional implementation — read once so an
// in-flight browser-back from a stale tab doesn't error, then cleared.
const LEGACY_RETURN_SECTION_KEY = "portfolio-return-section-id";

export default function Portfolio() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { startOutgoing } = useTransition();

  const [returnImage, setReturnImage] = useState(null);
  const [titleOverrides, setTitleOverrides] = useState({});

  // Hide the main content from the very first paint when we know a
  // return-navigation is pending. Without this, the initial render
  // (which happens BEFORE the resolver's scrollTo lands) paints at
  // scrollY=0 for one frame → the user sees section-01's book-trailer
  // video flash before the page snaps to the target project. Lazy state
  // initializer detects pending return SYNCHRONOUSLY (from location.state
  // for the BACK-link path, or sessionStorage for browser-back), so the
  // first render is invisible → no flicker.
  const [hidingForReturn, setHidingForReturn] = useState(() => {
    if (location.state?.slug || location.state?.scrollToSectionId) return true;
    if (navigationType === "POP") {
      try {
        return Boolean(
          sessionStorage.getItem(RETURN_SLUG_KEY) ||
            sessionStorage.getItem(LEGACY_RETURN_SECTION_KEY)
        );
      } catch {
        return false;
      }
    }
    return false;
  });
  // Settings map (index -> { hidden, order }) from /api/settings. Applied
  // by useMemo below to filter hidden projects and re-sort by admin order.
  // Missing entries fall back to visible + natural index order — so a fresh
  // install (empty collection) behaves exactly like the static list.
  const [settings, setSettings] = useState({});
  // Admin-created projects (from `/admin`). Empty array on a fresh install.
  const [customProjects, setCustomProjects] = useState([]);
  // Per-index overrides for the built-in 27 static projects — title and/or
  // hero media replacements coming from the /admin Edit form. Merged over
  // the static defaults in `visibleProjects` below.
  const [staticOverrides, setStaticOverrides] = useState({});
  // Number of admin fetches that have SETTLED (either resolved OR rejected —
  // rejections are non-fatal, we fall back to defaults). We use this instead
  // of `visibleProjects.length > 0` because the memo produces the static
  // list IMMEDIATELY from `projects.map(...)` even before any fetch resolves,
  // so length alone can't tell "static-order snapshot" from "final admin
  // reorder applied". The return-nav resolver waits for this to reach 3
  // before scrolling — otherwise it would consume against the pre-fetch
  // static order and land on the wrong hero once admin reordering arrived.
  // Three tracked fetches: fetchSettings, fetchCustomProjects, fetchStaticOverrides.
  const [adminFetchesSettled, setAdminFetchesSettled] = useState(0);

  // Pending return-navigation state. Populated once on mount from either
  // location.state (BACK link) or sessionStorage (browser back). We resolve
  // the recorded slug → current visible-list position → sectionId AFTER
  // the async admin fetches populate `visibleProjects`, so admin reordering
  // and hidden projects don't send the user to the wrong hero. Ref so it
  // survives re-renders without triggering them.
  //
  // Shape: { slug, image? } | null
  const pendingReturnRef = useRef(null);
  // Guards the [visibleProjects] resolver so it runs exactly once per mount.
  const returnConsumedRef = useRef(false);

  // Ensure the page starts at the very top on a fresh load. Browser scroll
  // restoration + scroll-snap can otherwise drop the user mid-snap on mount,
  // and the first swipe gets snapped to section 2 instead of staying on 1.
  //
  // Return-nav handling: capture the slug we want to land on into
  // `pendingReturnRef`. Freeze snap + scroll-behavior immediately so the
  // browser doesn't paint an intermediate section while we wait for admin
  // fetches to complete. The resolver effect below (keyed on
  // visibleProjects) does the actual scroll once the correct row's index
  // is known.
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    // Priority 1 — BACK link from CaseStudy (has slug + return-overlay image).
    const state = location.state;
    if (state?.slug) {
      pendingReturnRef.current = { slug: state.slug, image: state.image || null };
    } else if (state?.scrollToSectionId) {
      // Legacy state shape from an in-flight case-study page rendered by
      // an older bundle. Best-effort: try to derive the slug from the
      // section number → static projects array. Not perfect if admin
      // has reordered, but strictly better than the old behaviour.
      const match = state.scrollToSectionId.match(/section-(\d+)/);
      if (match) {
        const n = Number(match[1]) - 1;
        const legacyProject = projects[n];
        if (legacyProject?.slug) {
          pendingReturnRef.current = { slug: legacyProject.slug, image: state.image || null };
        }
      }
    } else if (navigationType === "POP") {
      // Priority 2 — browser back from case-study (no location.state).
      const slug = sessionStorage.getItem(RETURN_SLUG_KEY);
      if (slug) {
        pendingReturnRef.current = { slug, image: null };
      } else {
        // Legacy positional key — best-effort fallback for tabs that
        // navigated forward before the fix shipped.
        const legacy = sessionStorage.getItem(LEGACY_RETURN_SECTION_KEY);
        if (legacy) {
          const match = legacy.match(/section-(\d+)/);
          if (match) {
            const n = Number(match[1]) - 1;
            const legacyProject = projects[n];
            if (legacyProject?.slug) {
              pendingReturnRef.current = { slug: legacyProject.slug, image: null };
            }
          }
          sessionStorage.removeItem(LEGACY_RETURN_SECTION_KEY);
        }
      }
    }

    if (pendingReturnRef.current) {
      // Freeze snap/behaviour so nothing paints or animates while we wait
      // for admin fetches to resolve `visibleProjects`. Released by the
      // resolver effect (or the safety cleanup below).
      const html = document.documentElement;
      html.style.scrollBehavior = "auto";
      html.style.scrollSnapType = "none";
      // Clear location.state so a refresh doesn't re-trigger the return
      // path. sessionStorage RETURN_SLUG_KEY is cleared inside the
      // resolver effect once consumed.
      if (state) window.history.replaceState({}, "");
      return;
    }

    // No pending return actually got captured (lazy init may have flagged
    // hidingForReturn=true optimistically for e.g. a stale legacy state
    // that didn't resolve to a slug). Reveal main so we don't leave it
    // permanently hidden.
    setHidingForReturn(false);

    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
    // Mount-only on purpose: re-running on every navigationType / location
    // change would interfere with the state-driven scroll restoration
    // handled by the resolver effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTitles()
      .then((data) => setTitleOverrides(data || {}))
      .catch(() => {
        // Title overrides are a non-critical enhancement; fall back silently
        // to the hard-coded titles in projects.js if the endpoint is down.
      });
    // The three admin fetches drive `visibleProjects` (order + custom rows
    // + media overrides). We track settlement so the return-nav resolver
    // knows the visible list reflects the final admin state, not the
    // pre-fetch static-order snapshot.
    const bumpSettled = () => setAdminFetchesSettled((n) => n + 1);
    fetchSettings()
      .then((data) => setSettings(data || {}))
      .catch(() => {
        // Same fallback pattern: if the settings endpoint is unreachable,
        // render the full static list in its natural order.
      })
      .finally(bumpSettled);
    fetchCustomProjects()
      .then((list) => setCustomProjects(list || []))
      .catch(() => {
        // No custom projects yet — nothing to merge.
      })
      .finally(bumpSettled);
    fetchStaticOverrides()
      .then((data) => setStaticOverrides(data || {}))
      .catch(() => {
        // No overrides yet — static projects render with baked-in defaults.
      })
      .finally(bumpSettled);

    // Typography — SEPARATE from the 3 admin fetches gating the return-nav
    // resolver above. Ordering doesn't depend on font settings, so we
    // deliberately don't bump `adminFetchesSettled` here — the resolver
    // fires as soon as the ordering fetches settle, and typography lands
    // whenever it lands (usually a few frames later). On success we
    // load the Google Font AND inject a global <style> tag that applies
    // the settings to every [data-portfolio-headline] element on the page.
    // On failure we silently keep the source-code Tailwind defaults.
    fetchTypography()
      .then((typography) => {
        if (!typography || !typography.font_family) return;
        // Kick off the font download; the <link> hits the browser cache on
        // repeat visits so this is a one-time cost per browser session.
        loadGoogleFont(typography.font_family, [typography.font_weight || 400]);
        // Inject / replace the global stylesheet. The `!important` beats
        // Tailwind's arbitrary text-[...] utilities on the h2. Mobile
        // scales down by 65% so a 48px desktop headline doesn't cover half
        // the screen on a phone.
        const size = Number(typography.font_size) || 40;
        const styleId = "portfolio-headline-typography";
        const previous = document.getElementById(styleId);
        if (previous) previous.remove();
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          [data-portfolio-headline] {
            font-family: "${typography.font_family}", "Playfair Display", serif !important;
            font-size: ${size}px !important;
            font-weight: ${Number(typography.font_weight) || 400} !important;
            line-height: ${Number(typography.line_height) || 1} !important;
            letter-spacing: ${Number(typography.letter_spacing) || 0}em !important;
          }
          @media (max-width: 767px) {
            [data-portfolio-headline] {
              font-size: ${(size * 0.65).toFixed(2)}px !important;
            }
          }
        `.trim();
        document.head.appendChild(style);
      })
      .catch(() => {
        // Typography endpoint unreachable — keep source-code defaults.
      });
    // Run once on mount; fetchTitles / fetchSettings / fetchCustomProjects
    // are module-scope imports, setters are stable — none of these belong
    // in the deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply admin visibility + ordering to the merged (static + custom) project
  // list. Static projects preserve their ORIGINAL index (`originalIndex`)
  // because title overrides, session-storage return keys, and the
  // section-XX id attributes are all keyed on it. Custom projects are
  // identified by their uuid `id` and mapped into the same shape
  // PortfolioSection expects (title / media / filter / slug).
  const visibleProjects = useMemo(() => {
    const staticRows = projects.map((project, originalIndex) => {
      // If the admin has replaced the hero image (and possibly changed the
      // media type) for this static project, splice those values into the
      // media object BEFORE handing it to PortfolioSection. Preview / snap
      // fields on `media` remain untouched so the ambient hero-video
      // preview logic still applies to any project that had it originally.
      const ov = staticOverrides[String(originalIndex)] || {};
      let mergedProject = project;
      if (ov.media_src || ov.media_type || ov.media_poster) {
        const nextMedia = { ...(project.media || {}) };
        if (ov.media_type) nextMedia.type = ov.media_type;
        if (ov.media_src) {
          nextMedia.src = ov.media_src;
          // A replaced src invalidates the baked-in preview snippets
          // (those were pre-encoded from the ORIGINAL video). Drop them
          // so the browser doesn't paint the old preview under the new
          // hero.
          delete nextMedia.preview;
        }
        if (ov.media_poster) nextMedia.poster = ov.media_poster;
        mergedProject = { ...project, media: nextMedia };
      }
      return {
        project: mergedProject,
        originalIndex,
        customId: null,
        titleOverride: ov.title,
        hidden: Boolean(settings[String(originalIndex)]?.hidden),
        order:
          typeof settings[String(originalIndex)]?.order === "number"
            ? settings[String(originalIndex)].order
            : originalIndex,
      };
    });
    // Reshape custom docs into the same {project, ...} row shape so the
    // downstream sort + render code is uniform. `originalIndex: null`
    // signals "no title override lookup" and "no static case-study slug".
    const customRows = customProjects.map((c) => {
      // Derive a hero image for the case-study transition. For videos we
      // prefer the poster (still frame) so the cross-fade lands on a
      // paintable image; for image projects the src itself is the hero.
      const heroImage = c.media_poster || c.media_src || "";
      return {
        project: {
          title: c.title || "Untitled",
          slug: c.slug || "",
          // Enables click-to-subpage routing — PortfolioSection gates the
          // click on `project.slug`, and Portfolio.handleProjectClick
          // gates on `project.transitionImage`.
          transitionImage: heroImage,
          media: {
            type: c.media_type || "image",
            src: c.media_src || "",
            ...(c.media_poster ? { poster: c.media_poster } : {}),
          },
          filter: c.filter || "none",
        },
        originalIndex: null,
        customId: c.id,
        titleOverride: undefined,
        hidden: Boolean(c.hidden),
        // Custom projects default to `order: 9999` server-side so they land
        // at the end until the admin drags them into position.
        order: typeof c.order === "number" ? c.order : 9999,
      };
    });
    return [...staticRows, ...customRows]
      .filter((r) => !r.hidden)
      .sort((a, b) => a.order - b.order);
  }, [settings, customProjects, staticOverrides]);

  const handleProjectClick = (project, index) => {
    if (!project.slug || !project.transitionImage) return;
    // Persist the STABLE slug so browser-back from the case-study restores
    // the correct hero even if admin has since reordered / hidden / added
    // projects between click and return. Resolved to a section id after
    // `visibleProjects` is populated on the return mount. `index` is
    // intentionally NOT used — see comment near RETURN_SLUG_KEY.
    sessionStorage.setItem(RETURN_SLUG_KEY, project.slug);
    startOutgoing(project.transitionImage, project.slug);
  };

  // Resolver: consume `pendingReturnRef` once ALL admin fetches have settled
  // AND `visibleProjects` reflects the final admin ordering. Waiting for
  // `adminFetchesSettled >= 3` is critical — the memo produces the static
  // list immediately from `projects.map()` before any fetch resolves, so a
  // resolver that fired on the first `visibleProjects` render would find
  // the slug at its **static-order** position and consume against a stale
  // snapshot, landing the user on the wrong hero once reordering arrived.
  useLayoutEffect(() => {
    const pending = pendingReturnRef.current;
    if (!pending || returnConsumedRef.current) return;
    if (adminFetchesSettled < 3) return; // wait for the real order
    if (!visibleProjects || visibleProjects.length === 0) return;

    // Find the project by slug across static + custom rows.
    const idx = visibleProjects.findIndex(
      (r) => r.project?.slug && r.project.slug === pending.slug
    );

    // With `adminFetchesSettled >= 3` above, the visible list reflects the
    // final admin state. If the slug still isn't there, the project was
    // hidden or deleted — release the snap freeze and fall back to top so
    // the page becomes usable.
    if (idx < 0) {
      returnConsumedRef.current = true;
      sessionStorage.removeItem(RETURN_SLUG_KEY);
      const html = document.documentElement;
      html.style.removeProperty("scroll-behavior");
      html.style.removeProperty("scroll-snap-type");
      window.scrollTo(0, 0);
      // No target to land on — reveal main so the page is usable.
      setHidingForReturn(false);
      return;
    }

    returnConsumedRef.current = true;
    sessionStorage.removeItem(RETURN_SLUG_KEY);

    const sectionId = `section-${String(idx + 1).padStart(2, "0")}`;
    const target = document.getElementById(sectionId);
    const html = document.documentElement;

    if (!target) {
      // Row exists in state but DOM node hasn't rendered yet. Give React
      // one more frame — clear the consumed flag and bail so the next
      // render retries.
      returnConsumedRef.current = false;
      return;
    }

    // Show the return-overlay if we came in via the BACK link (state.image).
    if (pending.image) setReturnImage(pending.image);

    // Two-step scroll pin: initial jump + re-pin on next frame. Then the
    // returnImage cleanup effect (or the immediate release below) drops
    // the snap freeze — same iOS-Safari-safe pattern as before.
    window.scrollTo({ top: target.offsetTop, left: 0, behavior: "auto" });
    const repin = () => {
      window.scrollTo({ top: target.offsetTop, left: 0, behavior: "auto" });
    };
    const raf = requestAnimationFrame(repin);
    const fallback = setTimeout(repin, 120);

    // Reveal main AFTER the scroll pin has landed — one rAF puts us safely
    // past the first commit at the new scroll position so no flicker of
    // section-01 can happen. Nested rAF is deliberate: rAF1 commits the
    // scrollTo above, rAF2 flips the opacity in the frame the target is
    // guaranteed to be painted at.
    const revealRaf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setHidingForReturn(false));
    });

    // If there's NO return-overlay (browser-back path), release the snap
    // freeze here — the returnImage effect below does the release when
    // there IS an overlay (BACK link path).
    if (!pending.image) {
      setTimeout(() => {
        html.style.removeProperty("scroll-behavior");
        html.style.removeProperty("scroll-snap-type");
      }, 140);
    }

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(revealRaf1);
      clearTimeout(fallback);
    };
    // pendingReturnRef and returnConsumedRef are refs (stable); we re-run
    // whenever the visible list OR the fetch-settled counter changes so we
    // don't fire against a pre-fetch stale snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjects, adminFetchesSettled]);

  useEffect(() => {
    if (!returnImage) return;
    const t = setTimeout(() => {
      // Two-step release: first restore the snap rules and re-pin scroll to
      // the target offset, then in the NEXT animation frame clear the
      // returnImage so the overlay unmounts. This gives iOS Safari one full
      // frame to commit any snap recompute under the still-visible overlay,
      // eliminating the tiny vertical "jump" the user saw at the end of the
      // BACK transition on mobile.
      const html = document.documentElement;
      html.style.removeProperty("scroll-behavior");
      html.style.removeProperty("scroll-snap-type");
      requestAnimationFrame(() => setReturnImage(null));
    }, CROSSFADE_MS + 100);
    return () => clearTimeout(t);
    // setReturnImage is a stable setter; CROSSFADE_MS is a module-scope
    // constant; `t`, `html` are local handles — none belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnImage]);

  return (
    <div data-testid="portfolio-root" className="relative w-full bg-paper text-ink">
      <Header />
      <ScrollProgress />

      <main
        data-testid="portfolio-main"
        style={{
          // Kept invisible during the resolver wait to prevent section-01
          // from flashing before we scroll to the target project. Flipped
          // false inside the resolver's rAF chain right after the scroll
          // pin lands. Uses visibility (not display:none) so element
          // offsets remain valid — offsetTop must be correct for the
          // scrollTo() call. `opacity: 0` alone would still let the
          // browser autoplay the section-01 video (audible in some rare
          // cases). visibility:hidden pauses autoplay too.
          visibility: hidingForReturn ? "hidden" : "visible",
        }}
      >
        {visibleProjects.map(({ project, originalIndex, customId, titleOverride }, i) => (
          <PortfolioSection
            key={customId || project.slug || `section-${originalIndex ?? i}`}
            project={project}
            index={i}
            total={visibleProjects.length}
            onMediaClick={handleProjectClick}
            // Precedence for the displayed heading is centralised in
            // /app/frontend/src/lib/titleResolver.js so this page and
            // Admin.jsx (which shows the same value in the Edit input)
            // can't drift out of sync.
            displayTitle={
              originalIndex !== null
                ? resolveStaticTitle(originalIndex, { [String(originalIndex)]: { title: titleOverride } }, titleOverrides, project)
                : project.title
            }
          />
        ))}
      </main>

      {/* Return cross-fade (case study → index) — kept local since the source page is gone. */}
      <AnimatePresence>
        {returnImage && (
          <motion.div
            data-testid="case-study-return-overlay"
            className="fixed left-0 top-0 z-[100] pointer-events-none overflow-hidden"
            style={{
              height: "100lvh",
              // Match body-content width (= clientWidth, excludes scrollbar
              // gutter) so the underlying section sits at the same extent
              // when the overlay fades out. `--sbw` is set globally by
              // useScrollbarGutterVar().
              width: "calc(100vw - var(--sbw, 0px))"
            }}
          >
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ duration: CROSSFADE_MS / 1000, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0 bg-paper"
            />
            <motion.div
              initial={{ scale: 0.78 }}
              animate={{ scale: 1 }}
              transition={{ duration: CROSSFADE_MS / 1000, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0 origin-center overflow-hidden will-change-transform"
            >
              <div className="absolute inset-[-22%]">
                <img src={returnImage} alt="" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
